import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import SEO from '../components/SEO';
import { getGSTBreakdown } from '../utils/gstUtils';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(0);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [lightboxOrigin, setLightboxOrigin] = useState({ x: 50, y: 50 });
  const blockClickFromDoubleRef = useRef(false);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const productDoc = await getDoc(doc(db, 'products', id));
      
      if (productDoc.exists()) {
        const productData = { id: productDoc.id, ...productDoc.data() };
        setProduct(productData);
        
        // Set default size
        if (productData.sizes && productData.sizes.length > 0) {
          setSelectedSize(productData.sizes[0]);
        }
      } else {
        toast.error('Product not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Error loading product');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchRelatedProducts = useCallback(async () => {
    if (!product) return;
    
    try {
      // Get products from same brand and category
      const q = query(
        collection(db, 'products'),
        where('brand', '==', product.brand),
        where('category', '==', product.category),
        limit(4)
      );
      
      const querySnapshot = await getDocs(q);
      const related = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => p.id !== product.id); // Exclude current product
      
      setRelatedProducts(related);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  }, [product]);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id, fetchProduct]);

  useEffect(() => {
    if (product) {
      fetchRelatedProducts();
    }
  }, [product, fetchRelatedProducts]);

  // Disable body scroll when lightbox is open
  useEffect(() => {
    if (showLightbox) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [showLightbox]);

  // Keyboard controls for lightbox (Esc to close, arrows to navigate)
  useEffect(() => {
    const handleKey = (e) => {
      if (!showLightbox) return;
      if (e.key === 'Escape') {
        setShowLightbox(false);
      } else if (e.key === 'ArrowLeft') {
        if (product?.images?.length) {
          setLightboxImage((prev) => (prev > 0 ? prev - 1 : product.images.length - 1));
        }
      } else if (e.key === 'ArrowRight') {
        if (product?.images?.length) {
          setLightboxImage((prev) => (prev < product.images.length - 1 ? prev + 1 : 0));
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showLightbox, product]);

  const handleAddToCart = () => {
    if (!currentUser) {
      toast.error('Please login to add items to cart');
      return;
    }
    if (!selectedSize && product.sizes && product.sizes.length > 0) {
      toast.error('Please select a size');
      return;
    }

    addToCart(product, quantity, selectedSize);
  };

  const handleBuyNow = () => {
    if (!currentUser) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }

    handleAddToCart();
    navigate('/cart');
  };

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setMousePosition({ x, y });
  };

  const openLightbox = (index) => {
    setLightboxImage(index);
    setLightboxZoom(1);
    setLightboxOrigin({ x: 50, y: 50 });
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setLightboxZoom(1);
    setLightboxOrigin({ x: 50, y: 50 });
  };

  const handleMainImageDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    blockClickFromDoubleRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
    setIsZoomed((prev) => !prev);
    // unblock single click shortly after
    setTimeout(() => { blockClickFromDoubleRef.current = false; }, 250);
  };

  const discountPercentage = product?.discountedPrice
    ? Math.round(((product.price - product.discountedPrice) / product.price) * 100)
    : 0;

  // Calculate GST breakdown for product price
  const displayPrice = product?.discountedPrice || product?.price || 0;
  const gstBreakdown = displayPrice ? getGSTBreakdown(displayPrice) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-serif text-deep-brown">Product not found</h1>
            <Link to="/" className="text-gold hover:underline mt-4 inline-block">
              Return to homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Product Schema for SEO
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "description": product.description,
    "image": product.images || [],
    "brand": {
      "@type": "Brand",
      "name": product.brand
    },
    "offers": {
      "@type": "Offer",
      "price": product.discountedPrice || product.price,
      "priceCurrency": "INR",
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://houseofdeepak.in/product/${product.id}`,
      "seller": {
        "@type": "Organization",
        "name": "House of Deepak"
      }
    },
    "aggregateRating": product.rating ? {
      "@type": "AggregateRating",
      "ratingValue": product.rating,
      "reviewCount": product.reviewCount || 1
    } : undefined,
    "category": product.category
  };

  return (
    <>
    <SEO 
      title={product.title}
      description={`${product.description?.substring(0, 155)} - Buy ${product.title} from ${product.brand}. Price: ₹${product.discountedPrice || product.price}`}
      keywords={`${product.title}, ${product.category}, ${product.brand}, buy online, ${product.brand === 'House of Deepak' ? 'Indian ethnic wear' : 'western fashion'}`}
      image={product.images?.[0]}
      type="product"
      schema={productSchema}
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-warm-gray hover:text-deep-brown transition-colors">
                Home
              </Link>
            </li>
            <li className="text-warm-gray">/</li>
            <li>
              <Link 
                to={product.brand === 'House of Deepak' ? '/indian-wear' : '/western-wear'}
                className="text-warm-gray hover:text-deep-brown transition-colors"
              >
                {product.brand === 'House of Deepak' ? 'Indian Wear' : 'Western Wear'}
              </Link>
            </li>
            <li className="text-warm-gray">/</li>
            <li className="text-deep-brown font-medium">{product.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative">
              <div 
                className="relative w-full aspect-square overflow-hidden rounded-lg bg-white shadow-sm border border-warm-gray/20 cursor-zoom-in flex items-center justify-center"
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
                onMouseMove={handleMouseMove}
                onClick={() => { if (!blockClickFromDoubleRef.current) openLightbox(selectedImage); }}
                onDoubleClick={handleMainImageDoubleClick}
              >
                <LazyLoadImage
                  src={product.images?.[selectedImage] || '/api/placeholder/600/600'}
                  alt={product.title}
                  effect="blur"
                  className="w-full h-full object-cover transition-transform duration-300"
                  style={{
                    transform: isZoomed ? 'scale(2)' : 'scale(1)',
                    transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`
                  }}
                />
                
                {/* Discount Badge */}
                {discountPercentage > 0 && (
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    -{discountPercentage}%
                  </div>
                )}

                {/* Stock Badge */}
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    product.stock > 0 
                      ? 'bg-sage text-white' 
                      : 'bg-red-500 text-white'
                  }`}>
                    {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>

              {/* Navigation Arrows */}
              {product.images && product.images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage(prev => prev > 0 ? prev - 1 : product.images.length - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                  >
                    <svg className="w-5 h-5 text-deep-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setSelectedImage(prev => prev < product.images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                  >
                    <svg className="w-5 h-5 text-deep-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index 
                        ? 'border-gold shadow-md' 
                        : 'border-warm-gray/20 hover:border-warm-gray/40'
                    }`}
                  >
                    <LazyLoadImage
                      src={image}
                      alt={`${product.title} ${index + 1}`}
                      effect="blur"
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="space-y-6">
            {/* Product Title & SKU */}
            <div>
              <h1 className="text-3xl lg:text-4xl font-serif text-deep-brown mb-2">
                {product.title}
              </h1>
              <p className="text-warm-gray text-sm">SKU: {product.sku}</p>
            </div>

            {/* Price */}
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-bold text-deep-brown">
                ₹{product.discountedPrice || product.price}
              </span>
              {product.discountedPrice && (
                <span className="text-xl text-warm-gray line-through">
                  ₹{product.price}
                </span>
              )}
              {discountPercentage > 0 && (
                <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-medium">
                  {discountPercentage}% OFF
                </span>
              )}
            </div>
            
            {/* GST Info */}
            {gstBreakdown && (
              <div className="text-sm text-warm-gray">
                Inclusive of {gstBreakdown.gstPercentageText} GST (₹{gstBreakdown.gstAmount.toFixed(2)})
              </div>
            )}

            {/* Description */}
            <div>
              <h3 className="text-lg font-serif text-deep-brown mb-2">Description</h3>
              <p className="text-warm-gray leading-relaxed">{product.description}</p>
            </div>

            {/* Sizes */}
            {product.sizes && product.sizes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-serif text-deep-brown">Size</h3>
                  <Link 
                    to="/size-guide"
                    className="text-sm text-gold hover:text-warm-gray transition-colors font-medium flex items-center gap-1"
                  >
                    <span>Size Guide</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border rounded-md transition-all ${
                        selectedSize === size
                          ? 'border-gold bg-gold text-deep-brown font-medium'
                          : 'border-warm-gray/30 text-warm-gray hover:border-warm-gray'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <h3 className="text-lg font-serif text-deep-brown mb-3">Quantity</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-warm-gray/30 rounded-md flex items-center justify-center hover:border-warm-gray transition-colors"
                >
                  <svg className="w-4 h-4 text-warm-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="w-16 text-center text-lg font-medium text-deep-brown">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  disabled={quantity >= product.stock}
                  className="w-10 h-10 border border-warm-gray/30 rounded-md flex items-center justify-center hover:border-warm-gray transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4 text-warm-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-warm-gray mt-2">
                {product.stock} items available
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={currentUser ? handleAddToCart : () => toast.error('Please login to add items to cart')}
                disabled={product.stock === 0}
                className="w-full bg-gold text-deep-brown py-4 px-6 rounded-md font-medium text-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {product.stock === 0 ? 'Out of Stock' : (currentUser ? 'Add to Cart' : 'Login to Add')}
              </button>
              
              <button
                onClick={handleBuyNow}
                disabled={product.stock === 0}
                className="w-full bg-deep-brown text-cream py-4 px-6 rounded-md font-medium text-lg hover:bg-warm-gray transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentUser ? 'Buy Now' : 'Login to Buy'}
              </button>
            </div>

            {/* Product Details */}
            <div className="border-t border-warm-gray/20 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-warm-gray">Brand:</span>
                  <span className="ml-2 text-deep-brown font-medium">{product.brand}</span>
                </div>
                <div>
                  <span className="text-warm-gray">Category:</span>
                  <span className="ml-2 text-deep-brown font-medium">{product.category}</span>
                </div>
                <div>
                  <span className="text-warm-gray">Stock:</span>
                  <span className="ml-2 text-deep-brown font-medium">{product.stock} units</span>
                </div>
                <div>
                  <span className="text-warm-gray">SKU:</span>
                  <span className="ml-2 text-deep-brown font-medium">{product.sku}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-serif text-deep-brown mb-8 text-center">
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <motion.div
                  key={relatedProduct.id}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-warm-gray/20"
                  whileHover={{ y: -5 }}
                >
                  <Link to={`/product/${relatedProduct.id}`}>
                    <div className="relative w-full aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
                      <LazyLoadImage
                        src={relatedProduct.images?.[0] || '/api/placeholder/300/300'}
                        alt={relatedProduct.title}
                        effect="blur"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {relatedProduct.discountedPrice && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                          -{Math.round(((relatedProduct.price - relatedProduct.discountedPrice) / relatedProduct.price) * 100)}%
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-deep-brown group-hover:text-gold transition-colors line-clamp-2">
                        {relatedProduct.title}
                      </h3>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-lg font-bold text-deep-brown">
                          ₹{relatedProduct.discountedPrice || relatedProduct.price}
                        </span>
                        {relatedProduct.discountedPrice && (
                          <span className="text-sm text-warm-gray line-through">
                            ₹{relatedProduct.price}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <div className="relative mx-4 w-[min(92vw,1200px)] h-[min(92vh,92vw)] flex items-center justify-center">
              <img
                src={product.images?.[lightboxImage]}
                alt={product.title}
                className="w-full h-full object-contain select-none"
                style={{ transform: `scale(${lightboxZoom})`, transformOrigin: `${lightboxOrigin.x}% ${lightboxOrigin.y}%` }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setLightboxOrigin({ x, y });
                  setLightboxZoom((z) => (z === 1 ? 2 : 1));
                }}
              />
              
              {/* Close Button */}
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Navigation */}
              {product.images && product.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(prev => prev > 0 ? prev - 1 : product.images.length - 1);
                    }}
                    aria-label="Previous image"
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-3 sm:p-4 rounded-full bg-black/20 hover:bg-black/30"
                  >
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(prev => prev < product.images.length - 1 ? prev + 1 : 0);
                    }}
                    aria-label="Next image"
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-3 sm:p-4 rounded-full bg-black/20 hover:bg-black/30"
                  >
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default ProductDetail;